<template>
  <div class="coord-transform">
    <div class="top">
      <div class="left">
        <!-- 第一行：坐标系选择和输入 -->
        <div class="coord-row">
          <div class="input-group">
            <label>原坐标系：</label>
            <el-select v-model="fromCoord" placeholder="选择原坐标系" style="width: 180px;">
              <el-option label="WGS84 (GPS)" value="wgs84"></el-option>
              <el-option label="GCJ02 (高德/腾讯)" value="gcj02"></el-option>
              <el-option label="BD09 (百度)" value="bd09"></el-option>
            </el-select>
          </div>
          <div class="input-group">
            <label>原坐标：</label>
            <el-input
              v-model="inputCoord"
              placeholder="输入坐标 (经度,纬度)"
              clearable
              style="width: 360px;">
            </el-input>
            <el-button type="primary" size="small" @click="handleTransform" style="margin-left: 10px;">
              <i class="el-icon-switch"></i> 转换
            </el-button>
          </div>
        </div>
    
        <!-- 提示信息 -->
        <div class="coord-tip" v-if="inputCoord">
          <i class="el-icon-info"></i>
          <span>识别坐标：{{ parsedCoord || '格式错误，请输入"经度,纬度"格式' }}</span>
        </div>
    
        <!-- 转换结果展示 -->
        <div class="result-row" v-if="showResults">
          <div class="result-item">
            <div class="result-label">WGS84 (GPS)</div>
            <el-input v-model="resultWgs84" readonly size="small">
              <template slot="prepend"><i class="el-icon-location"></i></template>
            </el-input>
          </div>
          <div class="result-item">
            <div class="result-label">GCJ02 (高德)</div>
            <el-input v-model="resultGcj02" readonly size="small">
              <template slot="prepend"><i class="el-icon-location"></i></template>
            </el-input>
          </div>
          <div class="result-item">
            <div class="result-label">BD09 (百度)</div>
            <el-input v-model="resultBd09" readonly size="small">
              <template slot="prepend"><i class="el-icon-location"></i></template>
            </el-input>
          </div>
        </div>
    
        <!-- 位置逆解析结果 -->
        <div class="address-row" v-if="showResults">
          <div class="address-item">
            <div class="address-label">
              <i class="el-icon-office-building"></i>
              位置解析
              <el-tag v-if="addressLoading" size="mini" type="info">解析中...</el-tag>
              <el-tag v-else-if="addressInfo" size="mini" type="success">解析成功</el-tag>
            </div>
            <div class="address-content" v-loading="addressLoading">
              <div v-if="addressInfo" class="address-detail">
                <div class="address-main">天地图地址：{{ addressInfo.formatted_address || addressInfo.address || '未知地址' }}</div>
                <div v-if="addressInfo.addressComponent" class="address-components">
                  <el-tag size="mini" v-if="addressInfo.addressComponent.province">{{ addressInfo.addressComponent.province }}</el-tag>
                  <el-tag size="mini" v-if="addressInfo.addressComponent.city">{{ addressInfo.addressComponent.city }}</el-tag>
                  <el-tag size="mini" v-if="addressInfo.addressComponent.district">{{ addressInfo.addressComponent.district }}</el-tag>
                  <el-tag size="mini" v-if="addressInfo.addressComponent.street">{{ addressInfo.addressComponent.street }}</el-tag>
                  <el-tag size="mini" v-if="addressInfo.addressComponent.township">{{ addressInfo.addressComponent.township }}</el-tag>
                </div>
                <div v-if="addressInfo.nearestPoint" class="address-nearest">
                  <i class="el-icon-map-location"></i>
                  最近地标：{{ addressInfo.nearestPoint.name }} ({{ addressInfo.nearestPoint.distance }}米)
                </div>
              </div>
              <div v-else-if="addressError" class="address-error">
                <i class="el-icon-warning"></i> {{ addressError }}
              </div>
              <div v-else class="address-empty">
                <i class="el-icon-info"></i> 点击"解析位置"获取地址信息
              </div>
            </div>
            <el-button 
              type="primary" 
              size="small" 
              :loading="addressLoading"
              @click="resolveAddress"
              style="margin-top: 10px;"
              icon="el-icon-search">
              解析位置
            </el-button>
          </div>
        </div>
      </div>
      <div class="right">
        <!-- 外部工具链接 -->
        <div class="external-links">
          <div class="links-header">
            <i class="el-icon-link"></i>
            <span>外部坐标工具</span>
          </div>
          <div class="links-list">
            <el-link 
              href="https://www.bejson.com/convert/map/#google_vignette" 
              target="_blank" 
              type="primary"
              :underline="false">
              <i class="el-icon-position"></i>
              高德坐标拾取工具
            </el-link>
            <el-link 
              href="http://192.168.10.159:8801/tianditu/demo1.html" 
              target="_blank" 
              type="success"
              :underline="false">
              <i class="el-icon-position"></i>
              天地图示例
            </el-link>
            <el-link 
              href="https://lbs.baidu.com/maptool/getpoint" 
              target="_blank" 
              type="warning"
              :underline="false">
              <i class="el-icon-position"></i>
              百度坐标拾取工具
            </el-link>
            <el-link 
              href="https://tool.lu/coordinate/" 
              target="_blank" 
              type="warning"
              :underline="false">
              <i class="el-icon-position"></i>
              在线坐标转换工具
            </el-link>
          </div>
        </div>
      </div>
    </div>

    <!-- 地图展示区域 - 4个模块 -->
    <div class="map-grid">
      <!-- 模块1：原始坐标 -->
      <div class="map-box">
        <div class="map-title">
          <i class="el-icon-map-location"></i>
          原始坐标 ({{ getCoordName(fromCoord) }})
          <el-tag size="mini" type="info">{{ fromCoord === 'wgs84' ? '天地图底图' : '高德底图' }}</el-tag>
        </div>
        <div id="map-original" class="map-container"></div>
      </div>

      <!-- 模块2：高德坐标 -->
      <div class="map-box">
        <div class="map-title">
          <i class="el-icon-map-location"></i>
          高德坐标 (GCJ02)
          <el-tag size="mini" type="success">高德底图</el-tag>
        </div>
        <div id="map-gcj02" class="map-container"></div>
      </div>

      <!-- 模块3：百度坐标 -->
      <div class="map-box">
        <div class="map-title">
          <i class="el-icon-map-location"></i>
          百度坐标 (BD09)
          <el-tag size="mini" type="warning">高德底图</el-tag>
        </div>
        <div id="map-bd09" class="map-container"></div>
      </div>

      <!-- 模块4：天地图坐标 -->
      <div class="map-box">
        <div class="map-title">
          <i class="el-icon-map-location"></i>
          天地图坐标 (WGS84)
          <el-tag size="mini" type="primary">天地图底图</el-tag>
        </div>
        <div id="map-wgs84" class="map-container"></div>
      </div>
    </div>
  </div>
</template>

<script>
import AMapLoader from "@amap/amap-jsapi-loader";

export default {
  name: "CoordTransform",
  data() {
    return {
      fromCoord: 'wgs84',
      inputCoord: '',
      parsedCoord: '',
      showResults: false,
      resultWgs84: '',
      resultGcj02: '',
      resultBd09: '',
      currentLng: 0,
      currentLat: 0,
      AMap: null,
      // 4个地图实例
      mapOriginal: null,  // 模块1：原始坐标地图
      mapGcj02: null,     // 模块2：高德坐标地图
      mapBd09: null,      // 模块3：百度坐标地图
      mapWgs84: null,     // 模块4：天地图坐标地图
      // 4个标记实例
      markerOriginal: null,
      markerGcj02: null,
      markerBd09: null,
      markerWgs84: null,
      leafletReady: false,
      // 用于实时预览的变量
      toCoord: 'gcj02',
      outputCoord: '',
      // 位置逆解析相关
      addressLoading: false,
      addressInfo: null,
      addressError: '',
      // 天地图 Key
      tdtKey: '5548e2c926e88b724376d398ba5476ca'
    }
  },
  mounted() {
    // 初始化高德地图（模块2、3使用高德底图，模块1根据坐标系可能使用）
    this.initAMap();
    // 初始化 Leaflet（模块1、4使用天地图底图）
    this.loadLeaflet().then(() => {
      this.initTdtMaps();
    });
  },
  beforeDestroy() {
    // 销毁高德地图实例
    if (this.mapOriginal) {
      this.mapOriginal.destroy();
    }
    if (this.mapGcj02) {
      this.mapGcj02.destroy();
    }
    if (this.mapBd09) {
      this.mapBd09.destroy();
    }
    // 销毁 Leaflet 地图实例
    if (this.mapWgs84) {
      this.mapWgs84.remove();
    }
  },
  methods: {
    initAMap() {
      AMapLoader.load({
        key: "0826878f934aa6f773237d9c82dfcf45",
        version: '2.0',
        plugins: ['AMap.Scale', 'AMap.Marker']
      }).then((AMap) => {
        this.AMap = AMap;
        
        // 模块2：高德坐标地图（固定高德底图）
        this.mapGcj02 = new AMap.Map('map-gcj02', {
          zoom: 10,
          center: [116.397428, 39.90923],
          viewMode: '2D'
        });
        this.mapGcj02.addControl(new AMap.Scale());
        
        // 模块3：百度坐标地图（固定高德底图）
        this.mapBd09 = new AMap.Map('map-bd09', {
          zoom: 10,
          center: [116.397428, 39.90923],
          viewMode: '2D'
        });
        this.mapBd09.addControl(new AMap.Scale());
        
      }).catch((e) => {
        console.error('高德地图加载失败:', e);
      });
    },
    
    // 动态加载 Leaflet
    loadLeaflet() {
      return new Promise((resolve, reject) => {
        if (window.L) {
          this.leafletReady = true;
          resolve();
          return;
        }
        
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.onload = () => {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
          document.head.appendChild(link);
          this.leafletReady = true;
          resolve();
        };
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },
    
    // 初始化天地图（模块1、4使用）
    initTdtMaps() {
      if (!this.leafletReady || !window.L) return;
      
      const L = window.L;
      
      // 创建天地图图层的辅助函数
      const createTdtLayers = () => {
        const vecLayer = L.tileLayer(
          `https://t{s}.tianditu.gov.cn/DataServer?tk=${this.tdtKey}&T=vec_w&x={x}&y={y}&l={z}`,
          {
            subdomains: ['0', '1', '2', '3', '4', '5', '6', '7'],
            maxZoom: 18,
            minZoom: 3,
            attribution: '© 天地图'
          }
        );
        const cvaLayer = L.tileLayer(
          `https://t{s}.tianditu.gov.cn/DataServer?tk=${this.tdtKey}&T=cva_w&x={x}&y={y}&l={z}`,
          {
            subdomains: ['0', '1', '2', '3', '4', '5', '6', '7'],
            maxZoom: 18,
            minZoom: 3
          }
        );
        return [vecLayer, cvaLayer];
      };
      
      // 模块1：原始坐标地图（天地图底图）- 延迟初始化，根据坐标系决定
      // 模块4：天地图坐标地图（固定天地图底图）
      const [vecLayer4, cvaLayer4] = createTdtLayers();
      this.mapWgs84 = L.map('map-wgs84', {
        center: [39.90923, 116.397428],
        zoom: 10,
        layers: [vecLayer4, cvaLayer4]
      });
      L.control.scale({ position: 'bottomleft' }).addTo(this.mapWgs84);
    },
    
    // 处理转换按钮点击
    handleTransform() {
      const cleaned = this.cleanCoordString(this.inputCoord);
      if (!cleaned) {
        this.$toast.warning('请输入有效的坐标');
        return;
      }
      
      const parts = cleaned.split(',');
      if (parts.length !== 2) {
        this.$toast.warning('坐标格式错误，请输入"经度,纬度"格式');
        return;
      }
      
      const lng = parseFloat(parts[0]);
      const lat = parseFloat(parts[1]);
      
      if (isNaN(lng) || isNaN(lat)) {
        this.$toast.warning('坐标格式错误，请输入有效的数字');
        return;
      }
      
      this.currentLng = lng;
      this.currentLat = lat;
      this.parsedCoord = `${lng}, ${lat}`;
      
      // 根据原坐标系转换出三种结果
      let wgs84, gcj02, bd09;
      
      if (this.fromCoord === 'wgs84') {
        wgs84 = { lng, lat };
        gcj02 = this.wgs84ToGcj02(lng, lat);
        bd09 = this.wgs84ToBd09(lng, lat);
      } else if (this.fromCoord === 'gcj02') {
        wgs84 = this.gcj02ToWgs84(lng, lat);
        gcj02 = { lng, lat };
        bd09 = this.gcj02ToBd09(lng, lat);
      } else if (this.fromCoord === 'bd09') {
        wgs84 = this.bd09ToWgs84(lng, lat);
        gcj02 = this.bd09ToGcj02(lng, lat);
        bd09 = { lng, lat };
      }
      
      // 更新结果
      this.resultWgs84 = `${wgs84.lng.toFixed(6)}, ${wgs84.lat.toFixed(6)}`;
      this.resultGcj02 = `${gcj02.lng.toFixed(6)}, ${gcj02.lat.toFixed(6)}`;
      this.resultBd09 = `${bd09.lng.toFixed(6)}, ${bd09.lat.toFixed(6)}`;
      this.showResults = true;
      
      // 重置位置解析状态
      this.addressInfo = null;
      this.addressError = '';
      
      // 更新地图
      this.updateMaps(lng, lat, wgs84, gcj02, bd09);
    },
    
    // 获取坐标系名称
    getCoordName(type) {
      const names = {
        'wgs84': 'WGS84 (GPS)',
        'gcj02': 'GCJ02 (高德)',
        'bd09': 'BD09 (百度)'
      };
      return names[type] || type;
    },
    
    // 处理输入，自动清理格式
    handleInput() {
      const cleaned = this.cleanCoordString(this.inputCoord);
      if (cleaned) {
        const parts = cleaned.split(',');
        if (parts.length === 2) {
          const lng = parseFloat(parts[0]);
          const lat = parseFloat(parts[1]);
          if (!isNaN(lng) && !isNaN(lat)) {
            this.parsedCoord = `${lng}, ${lat}`;
            this.transformCoord();
            return;
          }
        }
      }
      this.parsedCoord = '';
      this.outputCoord = '';
    },
    
    // 清理坐标字符串
    cleanCoordString(str) {
      console.log('cleanCoordString', str);
      if (!str) return '';
      // 去除单引号、双引号、空格
      let cleaned = str.replace(/['"]/g, '').trim();
      // 去除多余空格
      cleaned = cleaned.replace(/\s+/g, '');
      // 去除 []
      cleaned = cleaned.replace(/[\[\]]/g, '');
      return cleaned;
    },
    
    // 坐标转换
    transformCoord() {
      const cleaned = this.cleanCoordString(this.inputCoord);
      if (!cleaned) {
        this.outputCoord = '';
        return;
      }
      
      const parts = cleaned.split(',');
      if (parts.length !== 2) {
        this.outputCoord = '';
        return;
      }
      
      let lng = parseFloat(parts[0]);
      let lat = parseFloat(parts[1]);
      
      if (isNaN(lng) || isNaN(lat)) {
        this.outputCoord = '';
        return;
      }
      
      // 转换逻辑
      let result;
      if (this.fromCoord === this.toCoord) {
        result = { lng, lat };
      } else if (this.fromCoord === 'wgs84') {
        if (this.toCoord === 'gcj02') {
          result = this.wgs84ToGcj02(lng, lat);
        } else if (this.toCoord === 'bd09') {
          result = this.wgs84ToBd09(lng, lat);
        }
      } else if (this.fromCoord === 'gcj02') {
        if (this.toCoord === 'wgs84') {
          result = this.gcj02ToWgs84(lng, lat);
        } else if (this.toCoord === 'bd09') {
          result = this.gcj02ToBd09(lng, lat);
        }
      } else if (this.fromCoord === 'bd09') {
        if (this.toCoord === 'wgs84') {
          result = this.bd09ToWgs84(lng, lat);
        } else if (this.toCoord === 'gcj02') {
          result = this.bd09ToGcj02(lng, lat);
        }
      }
      
      if (result) {
        this.outputCoord = `${result.lng.toFixed(6)}, ${result.lat.toFixed(6)}`;
        this.updateMap(result.lng, result.lat);
      }
    },
    
    // 更新地图标记（用于实时预览）
    updateMap(lng, lat) {
      // 简单更新高德坐标地图和天地图坐标地图作为预览
      const AMap = this.AMap;
      const L = window.L;
      
      // 更新高德坐标地图
      if (AMap && this.mapGcj02) {
        // 需要根据当前选择的原坐标系进行转换
        let gcjLng = lng;
        let gcjLat = lat;
        
        if (this.fromCoord === 'wgs84') {
          const gcj = this.wgs84ToGcj02(lng, lat);
          gcjLng = gcj.lng;
          gcjLat = gcj.lat;
        } else if (this.fromCoord === 'bd09') {
          const gcj = this.bd09ToGcj02(lng, lat);
          gcjLng = gcj.lng;
          gcjLat = gcj.lat;
        }
        
        if (this.markerGcj02) {
          this.markerGcj02.setPosition([gcjLng, gcjLat]);
        } else {
          this.markerGcj02 = new AMap.Marker({
            position: [gcjLng, gcjLat],
            map: this.mapGcj02
          });
        }
        this.mapGcj02.setCenter([gcjLng, gcjLat]);
        this.mapGcj02.setZoom(14);
      }
      
      // 更新天地图坐标地图
      if (L && this.mapWgs84) {
        // 需要转换为 WGS84
        let wgs84Lng = lng;
        let wgs84Lat = lat;
        
        if (this.fromCoord === 'gcj02') {
          const wgs = this.gcj02ToWgs84(lng, lat);
          wgs84Lng = wgs.lng;
          wgs84Lat = wgs.lat;
        } else if (this.fromCoord === 'bd09') {
          const wgs = this.bd09ToWgs84(lng, lat);
          wgs84Lng = wgs.lng;
          wgs84Lat = wgs.lat;
        }
        
        if (this.markerWgs84) {
          this.markerWgs84.setLatLng([wgs84Lat, wgs84Lng]);
        } else {
          this.markerWgs84 = L.marker([wgs84Lat, wgs84Lng]).addTo(this.mapWgs84);
        }
        this.mapWgs84.setView([wgs84Lat, wgs84Lng], 14);
      }
    },
    
    // 更新4个地图模块
    updateMaps(origLng, origLat, wgs84, gcj02, bd09) {
      const AMap = this.AMap;
      const L = window.L;
      
      // ========== 模块1：原始坐标 ==========
      // 规则：WGS84用天地图底图，其他用高德底图
      if (this.fromCoord === 'wgs84') {
        // WGS84 → 天地图底图
        this.initOriginalTdtMapIfNeeded();
        if (this.mapOriginal && L) {
          // 天地图使用 [lat, lng]
          if (this.markerOriginal) {
            this.markerOriginal.setLatLng([origLat, origLng]);
          } else {
            this.markerOriginal = L.marker([origLat, origLng]).addTo(this.mapOriginal);
          }
          this.mapOriginal.setView([origLat, origLng], 14);
        }
      } else {
        // GCJ02/BD09 → 高德底图
        if (AMap) {
          // 需要高德地图容器来显示原始坐标
          this.initOriginalGdMapIfNeeded();
          if (this.mapOriginal) {
            let displayLng = origLng;
            let displayLat = origLat;
            // BD09 需要转为 GCJ02 才能在高德地图上正确显示
            if (this.fromCoord === 'bd09') {
              const gcj = this.bd09ToGcj02(origLng, origLat);
              displayLng = gcj.lng;
              displayLat = gcj.lat;
            }
            if (this.markerOriginal) {
              this.markerOriginal.setPosition([displayLng, displayLat]);
            } else {
              this.markerOriginal = new AMap.Marker({
                position: [displayLng, displayLat],
                map: this.mapOriginal
              });
            }
            this.mapOriginal.setCenter([displayLng, displayLat]);
            this.mapOriginal.setZoom(14);
          }
        }
      }
      
      // ========== 模块2：高德坐标 (GCJ02) → 固定高德底图 ==========
      if (AMap && this.mapGcj02) {
        if (this.markerGcj02) {
          this.markerGcj02.setPosition([gcj02.lng, gcj02.lat]);
        } else {
          this.markerGcj02 = new AMap.Marker({
            position: [gcj02.lng, gcj02.lat],
            map: this.mapGcj02
          });
        }
        this.mapGcj02.setCenter([gcj02.lng, gcj02.lat]);
        this.mapGcj02.setZoom(14);
      }
      
      // ========== 模块3：百度坐标 (BD09) → 固定高德底图 ==========
      // 注意：BD09 在高德底图上显示需要转为 GCJ02
      if (AMap && this.mapBd09) {
        // 将 BD09 转为 GCJ02 才能在高德地图上正确显示
        const gcjFromBd = this.bd09ToGcj02(bd09.lng, bd09.lat);
        if (this.markerBd09) {
          this.markerBd09.setPosition([gcjFromBd.lng, gcjFromBd.lat]);
        } else {
          this.markerBd09 = new AMap.Marker({
            position: [gcjFromBd.lng, gcjFromBd.lat],
            map: this.mapBd09
          });
        }
        this.mapBd09.setCenter([gcjFromBd.lng, gcjFromBd.lat]);
        this.mapBd09.setZoom(14);
      }
      
      // ========== 模块4：天地图坐标 (WGS84) → 固定天地图底图 ==========
      if (L && this.mapWgs84) {
        // 天地图使用 [lat, lng]
        if (this.markerWgs84) {
          this.markerWgs84.setLatLng([wgs84.lat, wgs84.lng]);
        } else {
          this.markerWgs84 = L.marker([wgs84.lat, wgs84.lng]).addTo(this.mapWgs84);
        }
        this.mapWgs84.setView([wgs84.lat, wgs84.lng], 14);
      }
    },
    
    // 延迟初始化模块1的天地图（WGS84原始坐标）
    initOriginalTdtMapIfNeeded() {
      if (this.mapOriginal || !window.L) return;
      const L = window.L;
      const vecLayer = L.tileLayer(
        `https://t{s}.tianditu.gov.cn/DataServer?tk=${this.tdtKey}&T=vec_w&x={x}&y={y}&l={z}`,
        {
          subdomains: ['0', '1', '2', '3', '4', '5', '6', '7'],
          maxZoom: 18,
          minZoom: 3,
          attribution: '© 天地图'
        }
      );
      const cvaLayer = L.tileLayer(
        `https://t{s}.tianditu.gov.cn/DataServer?tk=${this.tdtKey}&T=cva_w&x={x}&y={y}&l={z}`,
        {
          subdomains: ['0', '1', '2', '3', '4', '5', '6', '7'],
          maxZoom: 18,
          minZoom: 3
        }
      );
      this.mapOriginal = L.map('map-original', {
        center: [39.90923, 116.397428],
        zoom: 10,
        layers: [vecLayer, cvaLayer]
      });
      L.control.scale({ position: 'bottomleft' }).addTo(this.mapOriginal);
    },
    
    // 延迟初始化模块1的高德地图（GCJ02/BD09原始坐标）
    initOriginalGdMapIfNeeded() {
      if (this.mapOriginal || !this.AMap) return;
      this.mapOriginal = new this.AMap.Map('map-original', {
        zoom: 10,
        center: [116.397428, 39.90923],
        viewMode: '2D'
      });
      this.mapOriginal.addControl(new this.AMap.Scale());
    },
    
    // 复制结果
    copyResult() {
      if (!this.outputCoord) return;
      
      const text = this.outputCoord;
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      
      try {
        document.execCommand('copy');
        this.$toast.success('已复制到剪贴板');
      } catch (err) {
        this.$toast.error('复制失败');
      } finally {
        document.body.removeChild(textarea);
      }
    },
    
    // ========== 位置逆解析 ==========
    
    /**
     * 天地图位置逆解析
     * 通过坐标获取地址信息
     */
    async resolveAddress() {
      if (!this.currentLng || !this.currentLat) {
        this.$toast.warning('请先进行坐标转换');
        return;
      }

      this.addressLoading = true;
      this.addressError = '';
      this.addressInfo = null;

      try {
        // 天地图逆解析需要 WGS84 坐标
        let wgs84Lng = this.currentLng;
        let wgs84Lat = this.currentLat;

        // 如果当前坐标不是 WGS84，需要转换
        if (this.fromCoord === 'gcj02') {
          const wgs84 = this.gcj02ToWgs84(this.currentLng, this.currentLat);
          wgs84Lng = wgs84.lng;
          wgs84Lat = wgs84.lat;
        } else if (this.fromCoord === 'bd09') {
          const wgs84 = this.bd09ToWgs84(this.currentLng, this.currentLat);
          wgs84Lng = wgs84.lng;
          wgs84Lat = wgs84.lat;
        }

        // 调用天地图逆解析 API
        const response = await fetch(
          `https://api.tianditu.gov.cn/geocoder?postStr={'lon':${wgs84Lng},'lat':${wgs84Lat},'ver':1}&type=geocode&tk=${this.tdtKey}`,
          { method: 'GET' }
        );

        const result = await response.json();
        
        if (result.status === '0' && result.result) {
          const data = result.result;
          this.addressInfo = {
            formatted_address: data.formatted_address || data.address,
            addressComponent: data.addressComponent || {},
            location: data.location,
            nearestPoint: data.nearestPoint
          };
        } else {
          this.addressError = result.msg || '位置解析失败';
        }
      } catch (error) {
        console.error('位置逆解析失败:', error);
        this.addressError = '网络请求失败，请稍后重试';
      } finally {
        this.addressLoading = false;
      }
    },

    // ========== 坐标转换算法 ==========
    
    // 定义一些常量
    // x_PI: 3.14159265358979324 * 3000.0 / 180.0,
    // PI: 3.1415926535897932384626,
    // a: 6378245.0,
    // ee: 0.00669342162296594323,
    
    // 判断是否在中国境内
    outOfChina(lng, lat) {
      return !(lng > 73.66 && lng < 135.05 && lat > 3.86 && lat < 53.55);
    },
    
    transformLat(lng, lat) {
      let ret = -100.0 + 2.0 * lng + 3.0 * lat + 0.2 * lat * lat + 0.1 * lng * lat + 0.2 * Math.sqrt(Math.abs(lng));
      ret += (20.0 * Math.sin(6.0 * lng * Math.PI) + 20.0 * Math.sin(2.0 * lng * Math.PI)) * 2.0 / 3.0;
      ret += (20.0 * Math.sin(lat * Math.PI) + 40.0 * Math.sin(lat / 3.0 * Math.PI)) * 2.0 / 3.0;
      ret += (160.0 * Math.sin(lat / 12.0 * Math.PI) + 320 * Math.sin(lat * Math.PI / 30.0)) * 2.0 / 3.0;
      return ret;
    },
    
    transformLng(lng, lat) {
      let ret = 300.0 + lng + 2.0 * lat + 0.1 * lng * lng + 0.1 * lng * lat + 0.1 * Math.sqrt(Math.abs(lng));
      ret += (20.0 * Math.sin(6.0 * lng * Math.PI) + 20.0 * Math.sin(2.0 * lng * Math.PI)) * 2.0 / 3.0;
      ret += (20.0 * Math.sin(lng * Math.PI) + 40.0 * Math.sin(lng / 3.0 * Math.PI)) * 2.0 / 3.0;
      ret += (150.0 * Math.sin(lng / 12.0 * Math.PI) + 300.0 * Math.sin(lng / 30.0 * Math.PI)) * 2.0 / 3.0;
      return ret;
    },
    
    // WGS84 转 GCJ02
    wgs84ToGcj02(lng, lat) {
      if (this.outOfChina(lng, lat)) {
        return { lng, lat };
      }
      let dLat = this.transformLat(lng - 105.0, lat - 35.0);
      let dLng = this.transformLng(lng - 105.0, lat - 35.0);
      const radLat = lat / 180.0 * Math.PI;
      let magic = Math.sin(radLat);
      magic = 1 - 0.00669342162296594323 * magic * magic;
      const sqrtMagic = Math.sqrt(magic);
      dLat = (dLat * 180.0) / ((6378245.0 * (1 - 0.00669342162296594323)) / (magic * sqrtMagic) * Math.PI);
      dLng = (dLng * 180.0) / (6378245.0 / sqrtMagic * Math.cos(radLat) * Math.PI);
      const mgLat = lat + dLat;
      const mgLng = lng + dLng;
      return { lng: mgLng, lat: mgLat };
    },
    
    // GCJ02 转 WGS84
    gcj02ToWgs84(lng, lat) {
      if (this.outOfChina(lng, lat)) {
        return { lng, lat };
      }
      let dLat = this.transformLat(lng - 105.0, lat - 35.0);
      let dLng = this.transformLng(lng - 105.0, lat - 35.0);
      const radLat = lat / 180.0 * Math.PI;
      let magic = Math.sin(radLat);
      magic = 1 - 0.00669342162296594323 * magic * magic;
      const sqrtMagic = Math.sqrt(magic);
      dLat = (dLat * 180.0) / ((6378245.0 * (1 - 0.00669342162296594323)) / (magic * sqrtMagic) * Math.PI);
      dLng = (dLng * 180.0) / (6378245.0 / sqrtMagic * Math.cos(radLat) * Math.PI);
      const mgLat = lat + dLat;
      const mgLng = lng + dLng;
      return { lng: lng * 2 - mgLng, lat: lat * 2 - mgLat };
    },
    
    // GCJ02 转 BD09
    gcj02ToBd09(lng, lat) {
      const x_PI = 3.14159265358979324 * 3000.0 / 180.0;
      const z = Math.sqrt(lng * lng + lat * lat) + 0.00002 * Math.sin(lat * x_PI);
      const theta = Math.atan2(lat, lng) + 0.000003 * Math.cos(lng * x_PI);
      const bdLng = z * Math.cos(theta) + 0.0065;
      const bdLat = z * Math.sin(theta) + 0.006;
      return { lng: bdLng, lat: bdLat };
    },
    
    // BD09 转 GCJ02
    bd09ToGcj02(lng, lat) {
      const x_PI = 3.14159265358979324 * 3000.0 / 180.0;
      const x = lng - 0.0065;
      const y = lat - 0.006;
      const z = Math.sqrt(x * x + y * y) - 0.00002 * Math.sin(y * x_PI);
      const theta = Math.atan2(y, x) - 0.000003 * Math.cos(x * x_PI);
      const ggLng = z * Math.cos(theta);
      const ggLat = z * Math.sin(theta);
      return { lng: ggLng, lat: ggLat };
    },
    
    // WGS84 转 BD09
    wgs84ToBd09(lng, lat) {
      const gcj = this.wgs84ToGcj02(lng, lat);
      return this.gcj02ToBd09(gcj.lng, gcj.lat);
    },
    
    // BD09 转 WGS84
    bd09ToWgs84(lng, lat) {
      const gcj = this.bd09ToGcj02(lng, lat);
      return this.gcj02ToWgs84(gcj.lng, gcj.lat);
    }
  }
};
</script>

<style scoped>
.coord-transform {
  padding: 20px;
  background: var(--surface-1);
  border-radius: 8px;
  width: 100%;
}
.top {
  margin: 0 auto;
  width: 50%;
  border: 1px solid gray;
  border-radius: 10px;
  padding: 18px;
  display: flex;
  gap: 28px;
}
.left{
  width: 70%;
}
.coord-row {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.arrow-icon {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #409EFF;
  font-weight: 500;
}

.input-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.input-group label {
  color: #606266;
  font-size: 14px;
  white-space: nowrap;
}

.coord-tip {
  background: var(--surface-2);
  padding: 10px 15px;
  border-radius: 4px;
  color: #909399;
  font-size: 13px;
  margin-bottom: 20px;
}

.coord-tip i {
  margin-right: 8px;
}

.coord-tip span {
  color: #67C23A;
}



/* Leaflet 地图样式 */
:deep(.leaflet-container) {
  width: 100%;
  height: 100%;
}

:deep(.leaflet-control-attribution) {
  font-size: 10px;
}

.map-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: var(--surface-2);
  color: #909399;
}

.map-placeholder i {
  font-size: 48px;
  margin-bottom: 10px;
}

.map-placeholder p {
  margin: 0;
}

.external-links {
  margin-top: 30px;
  padding: 20px;
  background: var(--surface-2);
  border-radius: 8px;
  border: 1px dashed #d3d4d6;
}

/* 位置逆解析样式 */
.address-row {
  margin-top: 20px;
  padding: 20px;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e7ed 100%);
  border-radius: 8px;
  border: 1px solid rgba(31, 45, 61, 0.06);
}

.address-item {
  width: 100%;
}

.address-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 12px;
  font-size: 14px;
}

.address-label i {
  color: #409eff;
  font-size: 16px;
}

.address-content {
  min-height: 80px;
  background: var(--surface-1);
  border-radius: 6px;
  padding: 15px;
  border: 1px solid rgba(31, 45, 61, 0.06);
}

.address-detail {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.address-main {
  font-size: 16px;
  color: #303133;
  font-weight: 500;
  line-height: 1.6;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px dashed #e4e7ed;
}

.address-components {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.address-components .el-tag {
  margin: 0;
}

.address-nearest {
  font-size: 13px;
  color: #67c23a;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px;
  background: #f0f9ff;
  border-radius: 4px;
  margin-top: 10px;
}

.address-nearest i {
  color: #67c23a;
}

.address-error {
  color: #f56c6c;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.address-empty {
  color: #909399;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 100%;
}

.address-empty i {
  color: #c0c4cc;
}

.links-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 15px;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
}

.links-header i {
  color: #409EFF;
}

.links-list {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.links-list .el-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: var(--surface-1);
  border-radius: 4px;
  border: 1px solid rgba(31, 45, 61, 0.06);
  transition: all 0.3s ease;
}

.links-list .el-link:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.links-list .el-link i {
  font-size: 14px;
}

/* 地图网格布局 - 横向分布 */
.map-grid {
  display: flex;
  flex-direction: row;
  gap: 15px;
  margin-top: 20px;
  width: 100%;
}

.map-grid .map-box {
  flex: 1;
  min-width: 0; /* 允许flex子项缩小 */
  border: 1px solid rgba(31, 45, 61, 0.06);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.map-grid .map-container {
  height: 350px;
  width: 100%;
}

.map-grid .map-title {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  padding: 12px 15px;
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.map-grid .map-title i {
  font-size: 16px;
}

.map-grid .map-title .el-tag {
  margin-left: auto;
  font-size: 11px;
  height: 20px;
  line-height: 18px;
}

/* 响应式布局 */
@media (max-width: 1400px) {
  .map-grid {
    flex-wrap: wrap;
  }
  .map-grid .map-box {
    flex: 0 0 calc(50% - 7.5px);
  }
}

@media (max-width: 768px) {
  .map-grid .map-box {
    flex: 0 0 100%;
  }
}
</style>
